import { default as classNames } from "classnames";
import { useFormik } from "formik";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Button, Table } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import url from "../../module/fetch.tsx";
import { usePost } from "../Fetch/useFetch.tsx";
import useQueryParams from "../Fetch/useQueryParams.tsx";
import Pagination, {
  setOffsetToSearchParams,
  usePaginationState,
} from "./Pagination.tsx";
import styles from "./Table.module.css";
import { ButtonType } from "./TableActionColumn.tsx";

export const useSortBy = () => {
  const { sortBy: init = {} }: any = useQueryParams();
  const [sortBy, setSortBy] = useState(init);

  return [sortBy, setSortBy];
};

export const useFilterContext = (initialValues: any) => {
  const { filterBy } = useQueryParams();
  const formik = useFormik({
    initialValues: filterBy ?? initialValues,
    onSubmit: (values) => {
      alert(JSON.stringify(values, null, 2));
    },
    validateOnChange: false,
    validateOnBlur: true,
  });

  const { values, setValues, setFieldValue } = {
    values: formik.values,
    setValues: formik.setValues,
    setFieldValue: formik.setFieldValue,
  };

  const handleChange = (e: any) => {
    formik.handleChange(e);
  };

  return { values, setValues, formik, handleChange, setFieldValue };
};

function QueryTable({
  query,
  queryParams,
  asyncOptions,
  getList = (data) => data?.list,
  getCount = (data) => data?.count,
  columns = [],
  idName = "id",
  displayNone = false,
  className,
  trClassName,
  filterContext = {},
  filterValuesToVariable = (values: object[]) => values,
}: QueryTableType) {
  const { formik, setValues } = filterContext;
  const [tableState, setTableState] = useState([]);

  const getIdForKey = (row: any, append = "") => row[idName] + "" + append;

  const navigate = useNavigate();
  const { filterBy: initialFilterBy } = useQueryParams();
  const location = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [sortBy, setSortBy] = useSortBy();
  const [pagination, setPaginationTemp] = usePaginationState(); // eslint-disable-line react-hooks/rules-of-hooks
  let setPagination = setPaginationTemp;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  setPagination = ({ offset, limit }: { offset: number; limit: number }) => {
    setPaginationTemp({ limit, offset });
    navigate(
      {
        search: setOffsetToSearchParams(
          document.location.search,
          limit,
          offset,
        ),
      },
      {
        state: location.state,
      },
    );
  };

  const [filterBy, setFilterBy] = useState(
    filterValuesToVariable(initialFilterBy ?? {}),
  );

  // Fonction de mise à jour de l'URL
  const historyPushSync = () => {
    const f = filterValuesToVariable(formik?.values);
    const filter = Object.values(f).filter(Boolean).length
      ? JSON.stringify(f)
      : null;
    const sort = Object.values(sortBy).filter(Boolean).length
      ? JSON.stringify(sortBy)
      : null;
    const searchParams = new URLSearchParams(location?.search);
    if (
      filter !== searchParams.get("filterBy") ||
      sort !== searchParams.get("sortBy")
    ) {
      if (filter) {
        searchParams.set("filterBy", filter);
      } else {
        searchParams.delete("filterBy");
      }
      if (sort) {
        searchParams.set("sortBy", sort);
      } else {
        searchParams.delete("sortBy");
      }
      searchParams.set("limit", pagination.limit);
      searchParams.set("offset", pagination.offset ?? "0");
      navigate(
        {
          search: decodeURIComponent(searchParams.toString()),
        },
        {
          state: location.state,
        },
      );
      setFilterBy(filterValuesToVariable(formik?.values));

      // Si on change de filtres, on se remet à la première page
      setPagination({ offset: 0, limit: pagination.limit });
    } else if (
      searchParams.get("offset") !== pagination.offset.toString() ||
      searchParams.get("limit") !== pagination.limit.toString()
    ) {
      searchParams.set("limit", pagination.limit);
      searchParams.set("offset", pagination.offset ?? "0");
      navigate(
        {
          search: decodeURIComponent(searchParams.toString()),
        },
        {
          state: location.state,
        },
      );
    }
  };

  const debounceSearch = useDebouncedCallback(() => {
    historyPushSync();
  }, 500);

  // Quand les filtres changent, on debounce la mise à jour de l'URL
  // Quand le tri change, on met à jour immédiatement
  // biome-ignore lint/correctness/useExhaustiveDependencies: historyPushSync est une fonction recréée à chaque render, on ne veut pas l'ajouter comme dépendance
  useEffect(() => {
    historyPushSync();
  }, [sortBy]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: debounceSearch est stable et appelle toujours la dernière version de historyPushSync
  useEffect(() => {
    debounceSearch();
  }, [formik?.values]);

  const { isRejected, isLoading, data, run } = usePost(
    queryParams ? query + "?" + url`${queryParams}` : query,
    asyncOptions,
  );
  const firstMount = useRef(true);

  useEffect(() => {
    const fetchData = () => {
      // on ne veut pas relancer un chargement immédiatement si on passe un state parent
      if (firstMount.current) {
        firstMount.current = false;
      }
      if (
        JSON.stringify(tableState) !==
        JSON.stringify([filterBy, sortBy, pagination.limit, pagination.offset])
      ) {
        setTableState([filterBy, sortBy, pagination.limit, pagination.offset]);
        if (pagination.limit >= 0 && pagination.offset >= 0) {
          run({
            limit: pagination.limit,
            offset: pagination.offset,
            filterBy: filterBy,
            sortBy: sortBy,
          });
        }
      }
    };
    fetchData();
  }, [filterBy, sortBy, pagination.limit, pagination.offset, run, tableState]);

  if (isRejected) {
    return <div>Une erreur est survenue.</div>;
  }

  const makeRow = (row: any) => {
    return (
      <tr key={getIdForKey(row)} className={trClassName}>
        {columns.map((column, j) =>
          makeCell(column, row, getIdForKey(row, j.toString())),
        )}
      </tr>
    );
  };

  const makeCell = (column: any, row: any, key: string) => {
    const { Cell, accessor } = column;
    let res = null;
    let acc = null;
    if (typeof accessor === "function") {
      acc = accessor(row);
    } else {
      acc = row[accessor];
    }
    if (Cell) {
      res = <Cell {...{ value: acc, original: row }} />;
    } else {
      res = acc;
    }
    return (
      <td
        key={key}
        className={"m-0 p-0 ps-1"}
        style={{ verticalAlign: "middle", lineHeight: "normal" }}
      >
        {res}
      </td>
    );
  };

  const makeHeader = (column: any, key: string) => {
    const { Header, sortField, classNameHeader, width, Filter } = column;
    const isSortedAsc = sortBy[sortField] < 0;
    const isSortedDesc = sortBy[sortField] > 0;

    const toggleSort = () => {
      if (!sortField) {
        return;
      }

      setSortBy((prevSortBy) => {
        const newSortBy = { ...prevSortBy };

        const sortFields = Object.keys(newSortBy).filter(
          (field) => field !== sortField,
        );

        // Determine if current field is being sorted
        const isAsc = newSortBy[sortField] > 0;
        const isDesc = newSortBy[sortField] < 0;

        if (isAsc) {
          // From ASC to DESC
          sortFields.push(sortField);
          newSortBy[sortField] = `-${sortFields.length}`;
        } else if (isDesc) {
          // Remove sort field
          delete newSortBy[sortField];
        } else {
          // Add as ASC at the end
          sortFields.push(sortField);
          newSortBy[sortField] = `${sortFields.length}`;
        }

        // Recalculate all weights in order
        const finalSortBy: Record<string, string> = {};
        let i = 1;
        for (const field of Object.keys(newSortBy)) {
          const isNegative = newSortBy[field] < 0;
          finalSortBy[field] = `${isNegative ? "-" : ""}${i}`;
          i++;
        }

        return finalSortBy;
      });
    };
    return (
      <th
        key={key}
        className={classNames("text-center", classNameHeader)}
        style={{ width: width, verticalAlign: "top" }}
      >
        <div className={"d-flex flex-column h-100"}>
          <Row
            className="p-1"
            onClick={toggleSort}
            style={{ cursor: "pointer" }}
          >
            <Col xs={12} className="align-self-start text-nowrap">
              {Header}
              {sortField && (
                <span
                  className={classNames(
                    styles.sort,
                    {
                      [styles.sortActive]: isSortedAsc || isSortedDesc,
                    },
                    "m-1",
                  )}
                >
                  {isSortedAsc ? "▲" : isSortedDesc ? "▼" : "⬍"}
                </span>
              )}
            </Col>
          </Row>

          {Filter && (
            <Row>
              <Col xs={12}>
                {React.cloneElement(Filter, {
                  onChange: ({ name, value }) =>
                    formik.setFieldValue(name, value),
                  onBlur: formik.handleBlur,
                  defaultValue: formik?.values[Filter?.props?.name] || "",
                  value: formik?.values[Filter?.props?.name] || "",
                })}
              </Col>
            </Row>
          )}
        </div>
      </th>
    );
  };
  return (
    !displayNone && (
      <div>
        <div className="d-flex justify-content-end mb-2">
          <Button
            variant="danger"
            className="text-light"
            onClick={() => {
              const searchParams = new URLSearchParams(location?.search);
              searchParams.delete("filterBy");

              // Réinitialiser tous les filtres
              setFilterBy({});
              setSortBy({});
              setValues({});
              formik.setValues({});
              formik.resetForm({ values: {} });

              // On remet la pagination à zéro
              setPagination({ offset: 0, limit: pagination.limit });

              // On met à jour l'URL
              navigate(
                {
                  search: decodeURIComponent(searchParams.toString()),
                },
                {
                  state: location.state,
                },
              );
            }}
          >
            Effacer les filtres
          </Button>
        </div>
        <Table striped bordered hover className={className}>
          <thead>
            <tr>
              {columns.map((column, i) => makeHeader(column, i.toString()))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td>Chargement en cours</td>
              </tr>
            ) : getList(data)?.length === 0 ? (
              <tr>
                <td>Aucune donnée</td>
              </tr>
            ) : (
              <>{getList?.(data)?.map(makeRow)}</>
            )}
          </tbody>
        </Table>
        <Pagination
          isLoading={isLoading}
          count={getCount(data)}
          paginationState={[pagination, setPagination]}
          dataLength={getList(data)?.length}
          className={classNames({ "d-none": displayNone })}
        />
      </div>
    )
  );
}

type QueryTableType = {
  displayNone?: boolean;
  query: string;
  queryParams?: object[];
  asyncOptions?: object[];
  getList?: (data: any) => object[];
  getCount?: (data: any) => number;
  count?: number;
  paginationState?: [
    { offset: number; limit: number },
    ({ limit, offset }: { limit: number; offset: number }) => void,
  ];
  className?: string;
  columns: columnType[];
  idName: string;
  trClassName?: string;
  filterValuesToVariable?: any;
  filterContext?: any;
};

export type columnType = {
  Header?: string | ReactNode;
  Filter?: ReactNode;
  accessor: string | ((e: any) => any);
  sortField?: (() => string) | string;
  Cell?: (value: any) => ReactNode;
  className?: string;
  classNameHeader?: string;
  width?: number;
};
export type actionColumnType = columnType & { buttons: Array<ButtonType> };

export default QueryTable;
